
import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { useContentImages } from '@/hooks/useContentImages';
import clientsData from '@/data/clients.json';

interface Client {
  id: string
  name: string
  industry: string
  logo_url: string | null
  projects: string[]
  tech_stack: string[]
  website_url: string | null
  display_order: number
  is_active: boolean
}

const Clients = () => {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('All')
  const { images: clientImages } = useContentImages('clients')
  
  useEffect(() => {
    // Load static data
    const data = (clientsData as Client[])
      .filter(c => c.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setClients(data)
    setLoading(false)
  }, [])

  const industries = ['All', ...Array.from(new Set(clients.map(client => client.industry)))]
  
  const filteredClients = filter === 'All' 
    ? clients 
    : clients.filter(client => client.industry === filter)

  // Get hero image for clients section
  const heroImage = clientImages[0]

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div>Loading clients...</div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-800/70 to-blue-900/80 z-10"></div>
          <img
            src={heroImage?.image_url}
            alt={heroImage?.alt_text || ""}
            className="w-full h-full object-cover"
          />
        </div>
        {/* Background Pattern */}
        <div className="absolute inset-0 z-20 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-30 text-center pt-24 pb-24">
          <div className="max-w-4xl mx-auto animate-on-scroll">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Our Clients</h1>
              <p className="text-xl text-slate-200">
                 We've partnered with organizations across industries to deliver transformative IT solutions.
              </p>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-30">
          <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>
      
      {/* Client Logos */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold mb-12 text-center animate-on-scroll">Trusted By</h2>
          
          {clients.length === 0 ? (
            <div className="text-center text-gray-500">
              <p>No clients found. Please run the data fetch script to populate content.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center">
              {clients.slice(0, 5).map((client, index) => (
                <div 
                  key={client.id} 
                  className="flex justify-center p-6 grayscale hover:grayscale-0 transition-all animate-on-scroll"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <img 
                    src={client.logo_url || "/placeholder.svg"} 
                    alt={client.name} 
                    className="h-16 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* Client Details */}
      <section className="py-16 bg-goeltec-background">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
            <h2 className="text-3xl font-bold mb-4">Our Client Portfolio</h2>
            <p className="text-lg text-gray-700">
              Browse our clients by industry and discover the projects we've completed together.
            </p>
          </div>
          
          {/* Filter Buttons */}
          {industries.length > 1 && (
            <div className="flex flex-wrap justify-center gap-3 mb-12 animate-on-scroll">
              {industries.map((industry) => (
                <button
                  key={industry}
                  className={`px-4 py-2 rounded-full transition-colors ${
                    filter === industry 
                      ? 'bg-goeltec-blue text-white' 
                      : 'bg-white text-goeltec-text hover:bg-goeltec-lightblue'
                  }`}
                  onClick={() => setFilter(industry)}
                >
                  {industry}
                </button>
              ))}
            </div>
          )}
          
          {/* Clients Grid */}
          {filteredClients.length === 0 ? (
            <div className="text-center text-gray-500">
              <p>No clients found. Please run the data fetch script to populate content.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredClients.map((client, index) => (
                <div 
                  key={client.id}
                  className="bg-white rounded-lg overflow-hidden shadow-md animate-on-scroll card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="p-8 flex items-center justify-center bg-gray-50">
                    <img 
                      src={client.logo_url || "/placeholder.svg"} 
                      alt={client.name} 
                      className="h-16 w-auto"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{client.name}</h3>
                    <p className="text-gray-600 mb-4">Industry: {client.industry}</p>
                    
                    <h4 className="font-medium mb-2">Projects:</h4>
                    <ul className="list-disc list-inside mb-4 text-gray-600">
                      {client.projects.map((project, idx) => (
                        <li key={idx}>{project}</li>
                      ))}
                    </ul>
                    
                    <h4 className="font-medium mb-2">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {client.tech_stack.map((tech) => (
                        <span 
                          key={tech} 
                          className="text-xs bg-goeltec-lightblue text-goeltec-blue px-2 py-1 rounded"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Clients;
